import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button } from '@mui/material';

import ViewProductPageButton from '@components/ViewProductPageButton/ViewProductPageButton';
import { TInquiryStatus } from '@interfaces/inquiries';
import urls from '@routes/urls';
import { addSupplementToInquiryIsLoadingSelector } from '@store/features/inquiryDraft/addSupplementToInquiry/selectors';
import {
  inquiryDetailsIsLoadingSelector,
  inquiryDetailsSelector,
  isDietaryInListInInquiryDetailsSelector,
  isInStatusSelector,
} from '@store/features/inquiryDraft/inquiryDetails/selectors';
import { supplementDetailsSelector } from '@store/features/inquiryDraft/supplementDetails/selectors';
import useSupplementCategoryById from '@store/hooks/useSupplementCategoryById';

import { ISupplementSearchResultCardProps } from './SupplementSearchResultCard.interface';

import './style.scss';

const SupplementSearchResultCard: FC<ISupplementSearchResultCardProps> = ({
  isMedication = false,
  dietarySupplementName,
  addToInquiry,
}) => {
  const { t } = useTranslation();

  const dietarySupplementDetails = useSelector(supplementDetailsSelector);
  const inquiryDetails = useSelector(inquiryDetailsSelector);
  const category = useSupplementCategoryById(dietarySupplementDetails?.supplementCategoryId);
  const isInList = useSelector(isDietaryInListInInquiryDetailsSelector(dietarySupplementName));

  const isInDraft = useSelector(isInStatusSelector(TInquiryStatus.Draft));
  const addSupplementToInquiryIsLoading = useSelector(addSupplementToInquiryIsLoadingSelector);
  const inquiryDetailsIsLoading = useSelector(inquiryDetailsIsLoadingSelector);

  const isDisabled = useMemo(
    () =>
      addSupplementToInquiryIsLoading ||
      inquiryDetailsIsLoading ||
      isMedication ||
      isInList ||
      (!!inquiryDetails && !isInDraft),
    [
      addSupplementToInquiryIsLoading,
      inquiryDetails,
      inquiryDetailsIsLoading,
      isInDraft,
      isInList,
      isMedication,
    ],
  );

  return (
    <div className="supplement-search-result-card">
      {isMedication && (
        <>
          <Box className="supplement-search-result-card__section supplement-search-result-card__header">
            <Box component="img" src="/images/img-prescription-red.svg" />
            <p className="overline">{t('dietarySupplementInquiry.thisIsMedicationText')}</p>
          </Box>

          <Box className="supplement-search-result-card__status">
            <Button variant="text" href={urls.medications} target="_blank">
              <span className="supplement-search-result-card__button_text">
                {t('dietarySupplementInquiry.searchMedicationHere')}
              </span>
            </Button>
          </Box>

          <Box className="supplement-search-result-card__section">
            <p className="supplement-search-result-card__title overline">
              {t('dietarySupplementInquiry.medication')}
            </p>
            <h4>{dietarySupplementName}</h4>
          </Box>
        </>
      )}

      {!isMedication && (
        <>
          <Box className="supplement-search-result-card__section">
            <p className="supplement-search-result-card__title overline">
              {t('dietarySupplementInquiry.dietarySupplement')}
            </p>
            <h6>{dietarySupplementName}</h6>
          </Box>

          <Box className="supplement-search-result-card__section">
            <p className="supplement-search-result-card__title overline">
              {t('dietarySupplementInquiry.company')}
            </p>
            <h6>{dietarySupplementDetails?.companyName || '—'}</h6>
          </Box>

          <Box className="supplement-search-result-card__section">
            <p className="supplement-search-result-card__title overline">
              {t('dietarySupplementInquiry.category')}
            </p>
            <h6>{category?.name || '—'}</h6>
          </Box>

          {dietarySupplementDetails?.productLink && (
            <Box className="supplement-search-result-card__link-to-product">
              <ViewProductPageButton
                linkToProductPage={dietarySupplementDetails.productLink}
                className="supplement-search-result-card__btn"
              />
            </Box>
          )}
        </>
      )}

      <Box className="supplement-search-result-card__divider" />

      <Box className="supplement-search-result-card__actions">
        <Button
          variant="contained"
          disabled={isDisabled}
          onClick={() => !isMedication && addToInquiry?.(dietarySupplementDetails?.id!)}
        >
          {!isMedication && <AddIcon />}
          <span className="supplement-search-result-card__button_text">
            {!isMedication
              ? t('dietarySupplementInquiry.addToInquiry')
              : t('dietarySupplementInquiry.cannotBeAddedToInquiry')}
          </span>
        </Button>
      </Box>
    </div>
  );
};

export default SupplementSearchResultCard;
